/**
 * Google Analytics 4 Event Tracking Composable
 *
 * This composable provides methods to track various e-commerce events
 * following Google Analytics 4 specifications.
 *
 * Reference: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */

export function useGoogleAnalytics() {
    /**
     * Check if gtag is available
     */
    const isGtagAvailable = () => {
        return typeof window !== 'undefined' && typeof window.gtag === 'function';
    };

    /**
     * Format product item for GA4
     */
    const formatItem = (product, quantity = 1) => {
        return {
            item_id: product.code || product.uuid,
            item_name: product.name || product.title,
            price: parseFloat(product.price),
            quantity: quantity,
            item_category: product.category || 'Business Services',
            item_variant: product.variant || null,
        };
    };

    /**
     * Track view_item event when user views a product
     */
    const trackViewItem = (product) => {
        if (!isGtagAvailable()) {
            console.warn('Google Analytics not loaded');
            return;
        }

        const item = formatItem(product);

        window.gtag('event', 'view_item', {
            currency: 'USD',
            value: item.price,
            items: [item]
        });

        console.log('GA4: view_item tracked', item);
    };

    /**
     * Track add_to_cart event when user adds item to cart
     */
    const trackAddToCart = (product, quantity = 1) => {
        if (!isGtagAvailable()) {
            console.warn('Google Analytics not loaded');
            return;
        }

        const item = formatItem(product, quantity);

        window.gtag('event', 'add_to_cart', {
            currency: 'USD',
            value: item.price * item.quantity,
            items: [item]
        });

        console.log('GA4: add_to_cart tracked', item);
    };

    /**
     * Track remove_from_cart event when user removes item from cart
     */
    const trackRemoveFromCart = (product, quantity = 1) => {
        if (!isGtagAvailable()) {
            console.warn('Google Analytics not loaded');
            return;
        }

        const item = formatItem(product, quantity);

        window.gtag('event', 'remove_from_cart', {
            currency: 'USD',
            value: item.price * item.quantity,
            items: [item]
        });

        console.log('GA4: remove_from_cart tracked', item);
    };

    /**
     * Track begin_checkout event when user starts checkout
     */
    const trackBeginCheckout = (items, total) => {
        if (!isGtagAvailable()) {
            console.warn('Google Analytics not loaded');
            return;
        }

        const formattedItems = items.map(item => formatItem(item));

        window.gtag('event', 'begin_checkout', {
            currency: 'USD',
            value: parseFloat(total),
            items: formattedItems
        });

        console.log('GA4: begin_checkout tracked', { total, items: formattedItems });
    };

    /**
     * Track add_payment_info event when user adds payment information
     */
    const trackAddPaymentInfo = (paymentType, items, total) => {
        if (!isGtagAvailable()) {
            console.warn('Google Analytics not loaded');
            return;
        }

        const formattedItems = items.map(item => formatItem(item));

        window.gtag('event', 'add_payment_info', {
            currency: 'USD',
            value: parseFloat(total),
            payment_type: paymentType,
            items: formattedItems
        });

        console.log('GA4: add_payment_info tracked', { paymentType, total });
    };

    /**
     * Track purchase event when transaction is completed
     */
    const trackPurchase = (transactionId, items, total, tax = 0, shipping = 0) => {
        if (!isGtagAvailable()) {
            console.warn('Google Analytics not loaded');
            return;
        }

        const formattedItems = items.map(item => formatItem(item));

        window.gtag('event', 'purchase', {
            transaction_id: transactionId,
            value: parseFloat(total),
            tax: parseFloat(tax),
            shipping: parseFloat(shipping),
            currency: 'USD',
            items: formattedItems
        });

        console.log('GA4: purchase tracked', {
            transaction_id: transactionId,
            total,
            items: formattedItems
        });
    };

    /**
     * Track custom events
     */
    const trackEvent = (eventName, eventParams = {}) => {
        if (!isGtagAvailable()) {
            console.warn('Google Analytics not loaded');
            return;
        }

        window.gtag('event', eventName, eventParams);

        console.log(`GA4: ${eventName} tracked`, eventParams);
    };

    /**
     * Track page views (usually handled automatically by gtag)
     */
    const trackPageView = (pagePath, pageTitle) => {
        if (!isGtagAvailable()) {
            console.warn('Google Analytics not loaded');
            return;
        }

        window.gtag('event', 'page_view', {
            page_path: pagePath,
            page_title: pageTitle
        });

        console.log('GA4: page_view tracked', { pagePath, pageTitle });
    };

    /**
     * Track form submissions
     */
    const trackFormSubmit = (formName, formDestination = null) => {
        if (!isGtagAvailable()) {
            console.warn('Google Analytics not loaded');
            return;
        }

        window.gtag('event', 'form_submit', {
            form_name: formName,
            form_destination: formDestination
        });

        console.log('GA4: form_submit tracked', { formName, formDestination });
    };

    /**
     * Track file downloads
     */
    const trackFileDownload = (fileName, fileExtension) => {
        if (!isGtagAvailable()) {
            console.warn('Google Analytics not loaded');
            return;
        }

        window.gtag('event', 'file_download', {
            file_name: fileName,
            file_extension: fileExtension,
            link_url: window.location.href
        });

        console.log('GA4: file_download tracked', { fileName, fileExtension });
    };

    return {
        // E-commerce events
        trackViewItem,
        trackAddToCart,
        trackRemoveFromCart,
        trackBeginCheckout,
        trackAddPaymentInfo,
        trackPurchase,

        // General events
        trackEvent,
        trackPageView,
        trackFormSubmit,
        trackFileDownload,

        // Utility
        isGtagAvailable,
    };
}
